import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { cn } from "./cn";

const sideClasses = {
    left: "left-0 top-0 bottom-0 w-60 max-w-[80vw] border-r data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full",
    right: "right-0 top-0 bottom-0 w-60 max-w-[80vw] border-l data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full",
    bottom: "inset-x-0 bottom-0 max-h-[85dvh] rounded-t-[14px] border-t pb-[max(1rem,env(safe-area-inset-bottom))] data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full",
} as const;

/**
 * Side sheet (drawer) built on Base UI's Dialog — focus trap, Escape to close,
 * body scroll-lock, backdrop, and aria-modal all handled for us. Compose as:
 *
 *   <Drawer.Root open={open} onOpenChange={setOpen}>
 *     <Drawer.Trigger render={<button>Menu</button>} />
 *     <Drawer.Panel side="left">…</Drawer.Panel>
 *   </Drawer.Root>
 */
export const Drawer = {
    Root: BaseDialog.Root,
    Trigger: BaseDialog.Trigger,
    Close: BaseDialog.Close,
    Panel: function Panel({
        side = "left",
        className,
        children,
        ...props
    }: React.ComponentPropsWithoutRef<typeof BaseDialog.Popup> & {
        side?: keyof typeof sideClasses;
    }) {
        return (
            <BaseDialog.Portal>
                <BaseDialog.Backdrop
                    className={cn(
                        "fixed inset-0 z-[1000] bg-slate-900/30 backdrop-blur-[1px] transition-opacity duration-200",
                        "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
                    )}
                />
                <BaseDialog.Popup
                    className={cn(
                        "fixed z-[1001] flex flex-col overflow-y-auto " +
                            "border-border bg-surface shadow-xl outline-none " +
                            "transition-transform duration-200 ease-out",
                        sideClasses[side],
                        className as string,
                    )}
                    {...props}
                >
                    {side === "bottom" && (
                        <div aria-hidden className="mx-auto mb-2 mt-2 h-1 w-9 shrink-0 rounded-full bg-border" />
                    )}
                    {children}
                </BaseDialog.Popup>
            </BaseDialog.Portal>
        );
    },
};
